import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TuneIcon from "@mui/icons-material/Tune";
import {
  api,
  unwrap,
  type AgcPreferences,
  type UserPreferences,
} from "../api/client";
import type { PlaybackSession } from "../playback/session";

const KEY = ["preferences"];
const DEFAULT_AGC: AgcPreferences = {
  enabled: false,
  preset: "speech",
  target_dbfs: -20,
};

/**
 * The playback AGC (design §7.4.1): off or a preset, a target level, and a
 * "processed" chip with the gain applied while it is on. Saved per user.
 */
export function AgcControls({
  session,
  gainDb,
}: {
  session: PlaybackSession | null;
  gainDb: number | null;
}) {
  const qc = useQueryClient();
  const prefs = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/api/me/preferences")),
  });
  const save = useMutation({
    mutationFn: (body: UserPreferences) =>
      unwrap(api.PUT("/api/me/preferences", { body })),
    onError: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const agc = prefs.data?.agc ?? DEFAULT_AGC;
  const [target, setTarget] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    session?.setAgc({ ...agc, target_dbfs: target ?? agc.target_dbfs });
  }, [session, agc, target]);

  const change = (next: Partial<AgcPreferences>) => {
    const body = { ...prefs.data, agc: { ...agc, ...next } };
    qc.setQueryData(KEY, body);
    save.mutate(body);
  };

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
      <Select
        size="small"
        value={agc.enabled ? agc.preset : "off"}
        disabled={!prefs.isSuccess}
        onChange={(e) =>
          e.target.value === "off"
            ? change({ enabled: false })
            : change({
                enabled: true,
                preset: e.target.value as AgcPreferences["preset"],
              })
        }
        inputProps={{ "aria-label": "Automatic gain control" }}
      >
        <MenuItem value="off">AGC off</MenuItem>
        <MenuItem value="speech">AGC: Speech</MenuItem>
        <MenuItem value="gentle">AGC: Gentle</MenuItem>
      </Select>
      <Tooltip title="AGC settings">
        <IconButton
          size="small"
          aria-label="AGC settings"
          onClick={(e) => setAnchor(e.currentTarget)}
          disabled={!prefs.isSuccess}
        >
          <TuneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack sx={{ p: 2, width: 260 }}>
          <Typography variant="body2" id="agc-target">
            Target level: {target ?? agc.target_dbfs} dBFS
          </Typography>
          <Slider
            min={-40}
            max={-6}
            step={1}
            value={target ?? agc.target_dbfs}
            onChange={(_, v) => setTarget(v as number)}
            onChangeCommitted={(_, v) => {
              change({ target_dbfs: v as number });
              setTarget(null);
            }}
            aria-labelledby="agc-target"
          />
          <Typography variant="caption" color="text.secondary">
            Changes only what you hear; recordings are never altered.
          </Typography>
        </Stack>
      </Popover>
      {agc.enabled && (
        <Chip
          size="small"
          color="info"
          label={`Processed${gainDb === null ? "" : ` · ${gainDb >= 0 ? "+" : ""}${gainDb.toFixed(0)} dB`}`}
          aria-label="Playback is processed by the AGC"
        />
      )}
    </Stack>
  );
}
